/**
 * Utility functions for handling country flags and country detection
 * Based purely on location data - no external dependencies
 */

/**
 * Simple country code to country name mapping
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {string|null} Country name or null if not found
 */
export const getCountryName = countryCode => {
  if (!countryCode || typeof countryCode !== 'string') {
    return null;
  }

  const countryNames = {
    // Africa
    DZ: 'Algeria',
    AO: 'Angola',
    BJ: 'Benin',
    BW: 'Botswana',
    BF: 'Burkina Faso',
    BI: 'Burundi',
    CV: 'Cape Verde',
    CM: 'Cameroon',
    CF: 'Central African Republic',
    TD: 'Chad',
    KM: 'Comoros',
    CG: 'Republic of the Congo',
    CD: 'Democratic Republic of the Congo',
    CI: 'Ivory Coast',
    DJ: 'Djibouti',
    EG: 'Egypt',
    GQ: 'Equatorial Guinea',
    ER: 'Eritrea',
    SZ: 'Eswatini',
    ET: 'Ethiopia',
    GA: 'Gabon',
    GM: 'Gambia',
    GH: 'Ghana',
    GN: 'Guinea',
    GW: 'Guinea-Bissau',
    KE: 'Kenya',
    LS: 'Lesotho',
    LR: 'Liberia',
    LY: 'Libya',
    MG: 'Madagascar',
    MW: 'Malawi',
    ML: 'Mali',
    MR: 'Mauritania',
    MU: 'Mauritius',
    MA: 'Morocco',
    MZ: 'Mozambique',
    NA: 'Namibia',
    NE: 'Niger',
    NG: 'Nigeria',
    RW: 'Rwanda',
    ST: 'São Tomé and Príncipe',
    SN: 'Senegal',
    SC: 'Seychelles',
    SL: 'Sierra Leone',
    SO: 'Somalia',
    ZA: 'South Africa',
    SS: 'South Sudan',
    SD: 'Sudan',
    TZ: 'Tanzania',
    TG: 'Togo',
    TN: 'Tunisia',
    UG: 'Uganda',
    ZM: 'Zambia',
    ZW: 'Zimbabwe',

    // Asia
    AF: 'Afghanistan',
    AM: 'Armenia',
    AZ: 'Azerbaijan',
    BH: 'Bahrain',
    BD: 'Bangladesh',
    BT: 'Bhutan',
    BN: 'Brunei',
    KH: 'Cambodia',
    CN: 'China',
    GE: 'Georgia',
    IN: 'India',
    ID: 'Indonesia',
    IR: 'Iran',
    IQ: 'Iraq',
    IL: 'Israel',
    JP: 'Japan',
    JO: 'Jordan',
    KZ: 'Kazakhstan',
    KW: 'Kuwait',
    KG: 'Kyrgyzstan',
    LA: 'Laos',
    LB: 'Lebanon',
    MY: 'Malaysia',
    MV: 'Maldives',
    MN: 'Mongolia',
    MM: 'Myanmar',
    NP: 'Nepal',
    KP: 'North Korea',
    OM: 'Oman',
    PK: 'Pakistan',
    PS: 'Palestine',
    PH: 'Philippines',
    QA: 'Qatar',
    SA: 'Saudi Arabia',
    SG: 'Singapore',
    KR: 'South Korea',
    LK: 'Sri Lanka',
    SY: 'Syria',
    TW: 'Taiwan',
    TJ: 'Tajikistan',
    TH: 'Thailand',
    TL: 'East Timor',
    TR: 'Turkey',
    TM: 'Turkmenistan',
    AE: 'United Arab Emirates',
    UZ: 'Uzbekistan',
    VN: 'Vietnam',
    YE: 'Yemen',
    HK: 'Hong Kong',
    MO: 'Macau',

    // Europe
    AL: 'Albania',
    AD: 'Andorra',
    AT: 'Austria',
    BY: 'Belarus',
    BE: 'Belgium',
    BA: 'Bosnia and Herzegovina',
    BG: 'Bulgaria',
    HR: 'Croatia',
    CY: 'Cyprus',
    CZ: 'Czech Republic',
    DK: 'Denmark',
    EE: 'Estonia',
    FI: 'Finland',
    FR: 'France',
    DE: 'Germany',
    GR: 'Greece',
    HU: 'Hungary',
    IS: 'Iceland',
    IE: 'Ireland',
    IT: 'Italy',
    XK: 'Kosovo',
    LV: 'Latvia',
    LI: 'Liechtenstein',
    LT: 'Lithuania',
    LU: 'Luxembourg',
    MT: 'Malta',
    MD: 'Moldova',
    MC: 'Monaco',
    ME: 'Montenegro',
    NL: 'Netherlands',
    MK: 'North Macedonia',
    NO: 'Norway',
    PL: 'Poland',
    PT: 'Portugal',
    RO: 'Romania',
    RU: 'Russia',
    SM: 'San Marino',
    RS: 'Serbia',
    SK: 'Slovakia',
    SI: 'Slovenia',
    ES: 'Spain',
    SE: 'Sweden',
    CH: 'Switzerland',
    UA: 'Ukraine',
    GB: 'United Kingdom',
    VA: 'Vatican City',

    // North America
    CA: 'Canada',
    MX: 'Mexico',
    US: 'United States',

    // Central America & Caribbean
    AG: 'Antigua and Barbuda',
    BS: 'Bahamas',
    BB: 'Barbados',
    BZ: 'Belize',
    CR: 'Costa Rica',
    CU: 'Cuba',
    DM: 'Dominica',
    DO: 'Dominican Republic',
    SV: 'El Salvador',
    GD: 'Grenada',
    GT: 'Guatemala',
    HT: 'Haiti',
    HN: 'Honduras',
    JM: 'Jamaica',
    KN: 'Saint Kitts and Nevis',
    LC: 'Saint Lucia',
    VC: 'Saint Vincent and the Grenadines',
    NI: 'Nicaragua',
    PA: 'Panama',
    TT: 'Trinidad and Tobago',
    PR: 'Puerto Rico',

    // South America
    AR: 'Argentina',
    BO: 'Bolivia',
    BR: 'Brazil',
    CL: 'Chile',
    CO: 'Colombia',
    EC: 'Ecuador',
    FK: 'Falkland Islands',
    GF: 'French Guiana',
    GY: 'Guyana',
    PY: 'Paraguay',
    PE: 'Peru',
    SR: 'Suriname',
    UY: 'Uruguay',
    VE: 'Venezuela',

    // Oceania
    AU: 'Australia',
    FJ: 'Fiji',
    KI: 'Kiribati',
    MH: 'Marshall Islands',
    FM: 'Micronesia',
    NR: 'Nauru',
    NZ: 'New Zealand',
    PW: 'Palau',
    PG: 'Papua New Guinea',
    WS: 'Samoa',
    SB: 'Solomon Islands',
    TO: 'Tonga',
    TV: 'Tuvalu',
    VU: 'Vanuatu',
    NC: 'New Caledonia',
    PF: 'French Polynesia',
    CK: 'Cook Islands',
    NU: 'Niue',
    TK: 'Tokelau',
    WF: 'Wallis and Futuna',

    // Other territories and dependencies
    AX: 'Åland Islands',
    AS: 'American Samoa',
    AW: 'Aruba',
    BM: 'Bermuda',
    BV: 'Bouvet Island',
    IO: 'British Indian Ocean Territory',
    VG: 'British Virgin Islands',
    KY: 'Cayman Islands',
    CX: 'Christmas Island',
    CC: 'Cocos Islands',
    FO: 'Faroe Islands',
    GI: 'Gibraltar',
    GL: 'Greenland',
    GP: 'Guadeloupe',
    GU: 'Guam',
    GG: 'Guernsey',
    HM: 'Heard Island and McDonald Islands',
    IM: 'Isle of Man',
    JE: 'Jersey',
    MQ: 'Martinique',
    YT: 'Mayotte',
    MS: 'Montserrat',
    MP: 'Northern Mariana Islands',
    NF: 'Norfolk Island',
    PN: 'Pitcairn Islands',
    RE: 'Réunion',
    SH: 'Saint Helena',
    PM: 'Saint Pierre and Miquelon',
    GS: 'South Georgia',
    SJ: 'Svalbard and Jan Mayen',
    TC: 'Turks and Caicos Islands',
    UM: 'United States Minor Outlying Islands',
    VI: 'United States Virgin Islands',
    EH: 'Western Sahara',
    AQ: 'Antarctica',
    TF: 'French Southern Territories',
  };

  return countryNames[countryCode.toUpperCase()] || null;
};

/**
 * Get flag emoji for a country code
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {string} Flag emoji or empty string if not available
 */
export const getFlagEmoji = countryCode => {
  if (!countryCode || typeof countryCode !== 'string' || countryCode.length !== 2) {
    return '';
  }

  try {
    const upperCountryCode = countryCode.toUpperCase();

    // Convert country code to flag emoji using Unicode regional indicator symbols
    // A=U+1F1E6, B=U+1F1E7, ..., Z=U+1F1FF
    const codePoints = upperCountryCode.split('').map(char => {
      const charCode = char.charCodeAt(0);
      const flagCharCode = charCode - 65 + 0x1f1e6;
      return flagCharCode;
    });

    const flagEmoji = String.fromCodePoint(...codePoints);

    return flagEmoji;
  } catch (error) {
    console.warn('Error generating flag emoji for country code:', countryCode, error);
    return '';
  }
};

/**
 * Extract country code from a location address using comprehensive pattern matching
 * @param {string} address - Full address string
 * @returns {string|null} Country code or null if not found
 */
export const getCountryFromAddress = address => {
  if (!address || typeof address !== 'string') {
    return null;
  }

  // Clean up the address for better matching
  const cleanAddress = address.trim().toLowerCase();

  // Comprehensive country patterns - more variations and common spellings
  const countryPatterns = {
    // Major English-speaking countries
    'united states': 'US',
    usa: 'US',
    america: 'US',
    'u.s.a': 'US',
    'u.s.': 'US',
    'united kingdom': 'GB',
    uk: 'GB',
    'u.k.': 'GB',
    england: 'GB',
    scotland: 'GB',
    wales: 'GB',
    'northern ireland': 'GB',
    'great britain': 'GB',
    britain: 'GB',
    canada: 'CA',
    australia: 'AU',
    'new zealand': 'NZ',
    ireland: 'IE',
    eire: 'IE',

    // Western Europe
    france: 'FR',
    french: 'FR',
    francia: 'FR',
    germany: 'DE',
    deutschland: 'DE',
    german: 'DE',
    alemania: 'DE',
    spain: 'ES',
    españa: 'ES',
    spanish: 'ES',
    espagne: 'ES',
    italy: 'IT',
    italia: 'IT',
    italian: 'IT',
    italie: 'IT',
    netherlands: 'NL',
    holland: 'NL',
    dutch: 'NL',
    'pays-bas': 'NL',
    nederland: 'NL',
    belgium: 'BE',
    belgique: 'BE',
    belgië: 'BE',
    belgian: 'BE',
    austria: 'AT',
    österreich: 'AT',
    austrian: 'AT',
    autriche: 'AT',
    switzerland: 'CH',
    swiss: 'CH',
    schweiz: 'CH',
    suisse: 'CH',
    svizzera: 'CH',
    portugal: 'PT',
    portuguese: 'PT',
    portugues: 'PT',
    luxembourg: 'LU',
    luxemburg: 'LU',
    lëtzebuerg: 'LU',
    monaco: 'MC',

    // Northern Europe (Including Estonia and all variations!)
    sweden: 'SE',
    sverige: 'SE',
    swedish: 'SE',
    suède: 'SE',
    norway: 'NO',
    norge: 'NO',
    norwegian: 'NO',
    norvège: 'NO',
    denmark: 'DK',
    danmark: 'DK',
    danish: 'DK',
    danemark: 'DK',
    finland: 'FI',
    suomi: 'FI',
    finnish: 'FI',
    finlande: 'FI',
    iceland: 'IS',
    ísland: 'IS',
    icelandic: 'IS',
    islande: 'IS',
    estonia: 'EE',
    eesti: 'EE',
    estonian: 'EE',
    estonie: 'EE',
    estland: 'EE',
    latvia: 'LV',
    latvija: 'LV',
    latvian: 'LV',
    lettonie: 'LV',
    lithuania: 'LT',
    lietuva: 'LT',
    lithuanian: 'LT',
    lituanie: 'LT',

    // Central and Eastern Europe
    poland: 'PL',
    polska: 'PL',
    polish: 'PL',
    pologne: 'PL',
    'czech republic': 'CZ',
    czechia: 'CZ',
    české: 'CZ',
    czech: 'CZ',
    'république tchèque': 'CZ',
    slovakia: 'SK',
    slovensko: 'SK',
    slovak: 'SK',
    slovaquie: 'SK',
    hungary: 'HU',
    magyarország: 'HU',
    hungarian: 'HU',
    hongrie: 'HU',
    romania: 'RO',
    românia: 'RO',
    romanian: 'RO',
    roumanie: 'RO',
    bulgaria: 'BG',
    българия: 'BG',
    bulgarian: 'BG',
    bulgarie: 'BG',
    slovenia: 'SI',
    slovenija: 'SI',
    slovenian: 'SI',
    slovénie: 'SI',
    croatia: 'HR',
    hrvatska: 'HR',
    croatian: 'HR',
    croatie: 'HR',
    serbia: 'RS',
    srbija: 'RS',
    serbian: 'RS',
    serbie: 'RS',
    bosnia: 'BA',
    'bosnia and herzegovina': 'BA',
    bosna: 'BA',
    bosnie: 'BA',
    montenegro: 'ME',
    'crna gora': 'ME',
    monténégro: 'ME',
    'north macedonia': 'MK',
    macedonia: 'MK',
    македонија: 'MK',
    albania: 'AL',
    shqipëria: 'AL',
    albanian: 'AL',
    albanie: 'AL',
    kosovo: 'XK',
    kosova: 'XK',
    kosovë: 'XK',
    moldova: 'MD',
    moldovan: 'MD',
    moldavie: 'MD',
    ukraine: 'UA',
    україна: 'UA',
    ukrainian: 'UA',
    ukraine: 'UA',
    belarus: 'BY',
    беларусь: 'BY',
    belarusian: 'BY',
    bélarus: 'BY',
    russia: 'RU',
    'russian federation': 'RU',
    россия: 'RU',
    russian: 'RU',
    russie: 'RU',

    // Southern Europe
    greece: 'GR',
    ελλάδα: 'GR',
    greek: 'GR',
    grèce: 'GR',
    cyprus: 'CY',
    κύπρος: 'CY',
    cypriot: 'CY',
    chypre: 'CY',
    malta: 'MT',
    maltese: 'MT',
    malte: 'MT',
    'san marino': 'SM',
    vatican: 'VA',
    'vatican city': 'VA',

    // Middle East
    turkey: 'TR',
    türkiye: 'TR',
    turkish: 'TR',
    turquie: 'TR',
    israel: 'IL',
    ישראל: 'IL',
    israeli: 'IL',
    israël: 'IL',
    palestine: 'PS',
    palestinian: 'PS',
    فلسطين: 'PS',
    lebanon: 'LB',
    لبنان: 'LB',
    lebanese: 'LB',
    liban: 'LB',
    syria: 'SY',
    سوريا: 'SY',
    syrian: 'SY',
    syrie: 'SY',
    jordan: 'JO',
    الأردن: 'JO',
    jordanian: 'JO',
    jordanie: 'JO',
    iraq: 'IQ',
    العراق: 'IQ',
    iraqi: 'IQ',
    irak: 'IQ',
    iran: 'IR',
    ایران: 'IR',
    iranian: 'IR',
    persia: 'IR',
    persian: 'IR',
    'saudi arabia': 'SA',
    السعودية: 'SA',
    saudi: 'SA',
    'arabie saoudite': 'SA',
    'united arab emirates': 'AE',
    uae: 'AE',
    emirates: 'AE',
    الإمارات: 'AE',
    qatar: 'QA',
    قطر: 'QA',
    qatari: 'QA',
    kuwait: 'KW',
    الكويت: 'KW',
    kuwaiti: 'KW',
    koweït: 'KW',
    bahrain: 'BH',
    البحرين: 'BH',
    bahraini: 'BH',
    bahreïn: 'BH',
    oman: 'OM',
    عمان: 'OM',
    omani: 'OM',
    yemen: 'YE',
    اليمن: 'YE',
    yemeni: 'YE',
    yémen: 'YE',
    afghanistan: 'AF',
    افغانستان: 'AF',
    afghan: 'AF',

    // South Asia - VERY IMPORTANT FOR BANGLADESH!
    bangladesh: 'BD',
    বাংলাদেশ: 'BD',
    bengali: 'BD',
    bengladesh: 'BD',
    bangla: 'BD',
    pakistan: 'PK',
    پاکستان: 'PK',
    pakistani: 'PK',
    india: 'IN',
    भारत: 'IN',
    indian: 'IN',
    inde: 'IN',
    hindustan: 'IN',
    nepal: 'NP',
    नेपाल: 'NP',
    nepalese: 'NP',
    népal: 'NP',
    'sri lanka': 'LK',
    'ශ්‍රී ලංකා': 'LK',
    ceylon: 'LK',
    srilanka: 'LK',
    srilankan: 'LK',
    bhutan: 'BT',
    'འབྲུག་ཡུལ': 'BT',
    bhutanese: 'BT',
    bhoutan: 'BT',
    maldives: 'MV',
    maldivian: 'MV',
    maldive: 'MV',

    // East Asia
    china: 'CN',
    中国: 'CN',
    chinese: 'CN',
    chine: 'CN',
    prc: 'CN',
    japan: 'JP',
    日本: 'JP',
    japanese: 'JP',
    japon: 'JP',
    nippon: 'JP',
    'south korea': 'KR',
    korea: 'KR',
    한국: 'KR',
    korean: 'KR',
    'corée du sud': 'KR',
    'north korea': 'KP',
    dprk: 'KP',
    조선: 'KP',
    'corée du nord': 'KP',
    mongolia: 'MN',
    монгол: 'MN',
    mongolian: 'MN',
    mongolie: 'MN',
    taiwan: 'TW',
    台灣: 'TW',
    taiwanese: 'TW',
    formosa: 'TW',
    'hong kong': 'HK',
    香港: 'HK',
    hongkong: 'HK',
    macau: 'MO',
    macao: 'MO',
    澳門: 'MO',

    // Southeast Asia
    thailand: 'TH',
    ประเทศไทย: 'TH',
    thai: 'TH',
    thaïlande: 'TH',
    siam: 'TH',
    vietnam: 'VN',
    'việt nam': 'VN',
    vietnamese: 'VN',
    'viet nam': 'VN',
    myanmar: 'MM',
    မြန်မာ: 'MM',
    burma: 'MM',
    burmese: 'MM',
    birmanie: 'MM',
    cambodia: 'KH',
    កម្ពុជា: 'KH',
    cambodian: 'KH',
    kampuchea: 'KH',
    cambodge: 'KH',
    laos: 'LA',
    ລາວ: 'LA',
    laotian: 'LA',
    malaysia: 'MY',
    malaysian: 'MY',
    malaisie: 'MY',
    singapore: 'SG',
    singaporean: 'SG',
    singapour: 'SG',
    indonesia: 'ID',
    indonesian: 'ID',
    indonésie: 'ID',
    brunei: 'BN',
    bruneian: 'BN',
    philippines: 'PH',
    pilipinas: 'PH',
    filipino: 'PH',
    philippine: 'PH',
    'east timor': 'TL',
    'timor-leste': 'TL',
    timorese: 'TL',

    // Central Asia
    kazakhstan: 'KZ',
    қазақстан: 'KZ',
    kazakh: 'KZ',
    uzbekistan: 'UZ',
    oʻzbekiston: 'UZ',
    uzbek: 'UZ',
    ouzbékistan: 'UZ',
    turkmenistan: 'TM',
    türkmenistan: 'TM',
    turkmen: 'TM',
    tajikistan: 'TJ',
    тоҷикистон: 'TJ',
    tajik: 'TJ',
    tadjikistan: 'TJ',
    kyrgyzstan: 'KG',
    кыргызстан: 'KG',
    kyrgyz: 'KG',
    kirghizistan: 'KG',
    azerbaijan: 'AZ',
    azərbaycan: 'AZ',
    azerbaijani: 'AZ',
    azerbaïdjan: 'AZ',
    armenia: 'AM',
    հայաստան: 'AM',
    armenian: 'AM',
    arménie: 'AM',
    georgia: 'GE',
    საქართველო: 'GE',
    georgian: 'GE',
    géorgie: 'GE',

    // Americas
    mexico: 'MX',
    méxico: 'MX',
    mexican: 'MX',
    mexique: 'MX',
    brazil: 'BR',
    brasil: 'BR',
    brazilian: 'BR',
    brésil: 'BR',
    argentina: 'AR',
    argentinian: 'AR',
    argentine: 'AR',
    chile: 'CL',
    chilean: 'CL',
    chili: 'CL',
    colombia: 'CO',
    colombian: 'CO',
    colombie: 'CO',
    peru: 'PE',
    perú: 'PE',
    peruvian: 'PE',
    pérou: 'PE',
    venezuela: 'VE',
    venezuelan: 'VE',
    vénézuela: 'VE',
    uruguay: 'UY',
    uruguayan: 'UY',
    paraguay: 'PY',
    paraguayan: 'PY',
    bolivia: 'BO',
    bolivian: 'BO',
    bolivie: 'BO',
    ecuador: 'EC',
    ecuadorian: 'EC',
    équateur: 'EC',
    guyana: 'GY',
    guyanese: 'GY',
    guyane: 'GY',
    suriname: 'SR',
    surinamese: 'SR',
    'french guiana': 'GF',
    'guyane française': 'GF',

    // Central America and Caribbean
    guatemala: 'GT',
    guatemalan: 'GT',
    belize: 'BZ',
    belizean: 'BZ',
    'el salvador': 'SV',
    salvador: 'SV',
    salvadoran: 'SV',
    honduras: 'HN',
    honduran: 'HN',
    nicaragua: 'NI',
    nicaraguan: 'NI',
    'costa rica': 'CR',
    costarica: 'CR',
    'costa rican': 'CR',
    panama: 'PA',
    panamanian: 'PA',
    cuba: 'CU',
    cuban: 'CU',
    jamaica: 'JM',
    jamaican: 'JM',
    jamaïque: 'JM',
    haiti: 'HT',
    haitian: 'HT',
    haïti: 'HT',
    'dominican republic': 'DO',
    dominican: 'DO',
    'république dominicaine': 'DO',
    'puerto rico': 'PR',
    puertorican: 'PR',
    'porto rico': 'PR',
    'trinidad and tobago': 'TT',
    trinidadian: 'TT',
    tobagonian: 'TT',
    barbados: 'BB',
    barbadian: 'BB',
    barbade: 'BB',

    // Africa - More comprehensive
    egypt: 'EG',
    مصر: 'EG',
    egyptian: 'EG',
    égypte: 'EG',
    libya: 'LY',
    ليبيا: 'LY',
    libyan: 'LY',
    libye: 'LY',
    tunisia: 'TN',
    تونس: 'TN',
    tunisian: 'TN',
    tunisie: 'TN',
    algeria: 'DZ',
    الجزائر: 'DZ',
    algerian: 'DZ',
    algérie: 'DZ',
    morocco: 'MA',
    المغرب: 'MA',
    moroccan: 'MA',
    maroc: 'MA',
    sudan: 'SD',
    السودان: 'SD',
    sudanese: 'SD',
    soudan: 'SD',
    'south sudan': 'SS',
    'south sudanese': 'SS',
    'soudan du sud': 'SS',
    ethiopia: 'ET',
    ኢትዮጵያ: 'ET',
    ethiopian: 'ET',
    éthiopie: 'ET',
    eritrea: 'ER',
    eritrean: 'ER',
    érythrée: 'ER',
    somalia: 'SO',
    somalian: 'SO',
    somalie: 'SO',
    djibouti: 'DJ',
    djiboutian: 'DJ',
    kenya: 'KE',
    kenyan: 'KE',
    uganda: 'UG',
    ugandan: 'UG',
    ouganda: 'UG',
    tanzania: 'TZ',
    tanzanian: 'TZ',
    tanzanie: 'TZ',
    rwanda: 'RW',
    rwandan: 'RW',
    burundi: 'BI',
    burundian: 'BI',
    'democratic republic of congo': 'CD',
    drc: 'CD',
    'congo-kinshasa': 'CD',
    'republic of congo': 'CG',
    'congo-brazzaville': 'CG',
    congo: 'CG',
    'central african republic': 'CF',
    car: 'CF',
    centrafrique: 'CF',
    chad: 'TD',
    chadian: 'TD',
    tchad: 'TD',
    cameroon: 'CM',
    cameroonian: 'CM',
    cameroun: 'CM',
    nigeria: 'NG',
    nigerian: 'NG',
    nigéria: 'NG',
    niger: 'NE',
    nigerien: 'NE',
    'burkina faso': 'BF',
    burkinabé: 'BF',
    mali: 'ML',
    malian: 'ML',
    senegal: 'SN',
    senegalese: 'SN',
    sénégal: 'SN',
    gambia: 'GM',
    gambian: 'GM',
    gambie: 'GM',
    guinea: 'GN',
    guinean: 'GN',
    guinée: 'GN',
    'guinea-bissau': 'GW',
    'guinée-bissau': 'GW',
    'sierra leone': 'SL',
    'sierra leonean': 'SL',
    liberia: 'LR',
    liberian: 'LR',
    'ivory coast': 'CI',
    "côte d'ivoire": 'CI',
    ivorian: 'CI',
    ghana: 'GH',
    ghanaian: 'GH',
    togo: 'TG',
    togolese: 'TG',
    benin: 'BJ',
    beninese: 'BJ',
    bénin: 'BJ',
    mauritania: 'MR',
    mauritanian: 'MR',
    mauritanie: 'MR',
    'south africa': 'ZA',
    'south african': 'ZA',
    'afrique du sud': 'ZA',
    namibia: 'NA',
    namibian: 'NA',
    namibie: 'NA',
    botswana: 'BW',
    botswanan: 'BW',
    zimbabwe: 'ZW',
    zimbabwean: 'ZW',
    zambia: 'ZM',
    zambian: 'ZM',
    zambie: 'ZM',
    malawi: 'MW',
    malawian: 'MW',
    mozambique: 'MZ',
    mozambican: 'MZ',
    madagascar: 'MG',
    malagasy: 'MG',
    mauritius: 'MU',
    mauritian: 'MU',
    maurice: 'MU',
    seychelles: 'SC',
    seychellois: 'SC',
    comoros: 'KM',
    comorian: 'KM',
    comores: 'KM',

    // Oceania
    'papua new guinea': 'PG',
    png: 'PG',
    papuan: 'PG',
    fiji: 'FJ',
    fijian: 'FJ',
    fidji: 'FJ',
    'solomon islands': 'SB',
    'îles salomon': 'SB',
    vanuatu: 'VU',
    vanuatuan: 'VU',
    'new caledonia': 'NC',
    'nouvelle-calédonie': 'NC',
    samoa: 'WS',
    samoan: 'WS',
    tonga: 'TO',
    tongan: 'TO',
  };

  // Try exact and partial matches for country names
  for (const [country, code] of Object.entries(countryPatterns)) {
    // Exact match (case insensitive)
    if (cleanAddress.includes(country)) {
      return code;
    }
  }

  // Try to match country codes directly (e.g., "123 Main St, BD" or "Dhaka, BD")
  const countryCodePattern = /\b([A-Z]{2})\b/g;
  const matches = address.match(countryCodePattern);
  if (matches) {
    // Check if any of the matches are valid country codes (simple validation)
    const validCountryCodes = [
      'AD',
      'AE',
      'AF',
      'AG',
      'AI',
      'AL',
      'AM',
      'AO',
      'AQ',
      'AR',
      'AS',
      'AT',
      'AU',
      'AW',
      'AX',
      'AZ',
      'BA',
      'BB',
      'BD',
      'BE',
      'BF',
      'BG',
      'BH',
      'BI',
      'BJ',
      'BL',
      'BM',
      'BN',
      'BO',
      'BQ',
      'BR',
      'BS',
      'BT',
      'BV',
      'BW',
      'BY',
      'BZ',
      'CA',
      'CC',
      'CD',
      'CF',
      'CG',
      'CH',
      'CI',
      'CK',
      'CL',
      'CM',
      'CN',
      'CO',
      'CR',
      'CU',
      'CV',
      'CW',
      'CX',
      'CY',
      'CZ',
      'DE',
      'DJ',
      'DK',
      'DM',
      'DO',
      'DZ',
      'EC',
      'EE',
      'EG',
      'EH',
      'ER',
      'ES',
      'ET',
      'FI',
      'FJ',
      'FK',
      'FM',
      'FO',
      'FR',
      'GA',
      'GB',
      'GD',
      'GE',
      'GF',
      'GG',
      'GH',
      'GI',
      'GL',
      'GM',
      'GN',
      'GP',
      'GQ',
      'GR',
      'GS',
      'GT',
      'GU',
      'GW',
      'GY',
      'HK',
      'HM',
      'HN',
      'HR',
      'HT',
      'HU',
      'ID',
      'IE',
      'IL',
      'IM',
      'IN',
      'IO',
      'IQ',
      'IR',
      'IS',
      'IT',
      'JE',
      'JM',
      'JO',
      'JP',
      'KE',
      'KG',
      'KH',
      'KI',
      'KM',
      'KN',
      'KP',
      'KR',
      'KW',
      'KY',
      'KZ',
      'LA',
      'LB',
      'LC',
      'LI',
      'LK',
      'LR',
      'LS',
      'LT',
      'LU',
      'LV',
      'LY',
      'MA',
      'MC',
      'MD',
      'ME',
      'MF',
      'MG',
      'MH',
      'MK',
      'ML',
      'MM',
      'MN',
      'MO',
      'MP',
      'MQ',
      'MR',
      'MS',
      'MT',
      'MU',
      'MV',
      'MW',
      'MX',
      'MY',
      'MZ',
      'NA',
      'NC',
      'NE',
      'NF',
      'NG',
      'NI',
      'NL',
      'NO',
      'NP',
      'NR',
      'NU',
      'NZ',
      'OM',
      'PA',
      'PE',
      'PF',
      'PG',
      'PH',
      'PK',
      'PL',
      'PM',
      'PN',
      'PR',
      'PS',
      'PT',
      'PW',
      'PY',
      'QA',
      'RE',
      'RO',
      'RS',
      'RU',
      'RW',
      'SA',
      'SB',
      'SC',
      'SD',
      'SE',
      'SG',
      'SH',
      'SI',
      'SJ',
      'SK',
      'SL',
      'SM',
      'SN',
      'SO',
      'SR',
      'SS',
      'ST',
      'SV',
      'SX',
      'SY',
      'SZ',
      'TC',
      'TD',
      'TF',
      'TG',
      'TH',
      'TJ',
      'TK',
      'TL',
      'TM',
      'TN',
      'TO',
      'TR',
      'TT',
      'TV',
      'TW',
      'TZ',
      'UA',
      'UG',
      'UM',
      'US',
      'UY',
      'UZ',
      'VA',
      'VC',
      'VE',
      'VG',
      'VI',
      'VN',
      'VU',
      'WF',
      'WS',
      'YE',
      'YT',
      'ZA',
      'ZM',
      'ZW',
    ];

    for (const match of matches) {
      if (validCountryCodes.includes(match)) {
        return match;
      }
    }
  }

  return null;
};

/**
 * Reverse geocoding: Convert lat/lng coordinates to address using Mapbox API
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<string|null>} Promise that resolves to address string or null
 */
export const reverseGeocode = async (lat, lng) => {
  try {
    // Get Mapbox access token from environment variables
    const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

    if (!accessToken) {
      console.warn('Mapbox access token not found in environment variables');
      return null;
    }

    // Mapbox Geocoding API - note: longitude comes first in Mapbox
    const response = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${accessToken}&types=country,region,place`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      console.warn(
        'Mapbox reverse geocoding request failed:',
        response.status,
        response.statusText
      );
      return null;
    }

    const data = await response.json();

    if (data && data.features && data.features.length > 0) {
      // Get the most relevant feature (usually the first one)
      const feature = data.features[0];
      const placeName = feature.place_name || feature.text;

      if (placeName) {
        return placeName;
      }

      // Try to extract country from context
      if (feature.context) {
        const countryContext = feature.context.find(ctx => ctx.id && ctx.id.startsWith('country'));
        if (countryContext && countryContext.text) {
          return countryContext.text;
        }
      }
    }

    return null;
  } catch (error) {
    console.warn('Error in Mapbox reverse geocoding:', error);
    return null;
  }
};

/**
 * Get country information from geolocation coordinates using geographic boundaries
 * Now includes ALL countries with comprehensive coverage
 * @param {Object} geolocation - Geolocation object with lat/lng
 * @param {number} geolocation.lat - Latitude
 * @param {number} geolocation.lng - Longitude
 * @returns {Promise<string|null>} Promise that resolves to country code or null
 */
export const getCountryFromGeolocation = async geolocation => {
  if (!geolocation || typeof geolocation.lat !== 'number' || typeof geolocation.lng !== 'number') {
    return null;
  }

  try {
    const { lat, lng } = geolocation;

    // IMPORTANT: Check smaller/specific countries FIRST before larger ones
    // This prevents overlapping issues like Bangladesh being caught by China

    // Small Island Nations and City States FIRST
    if (lat >= 1.2 && lat <= 1.5 && lng >= 103.6 && lng <= 104.0) {
      return 'SG'; // Singapore
    }
    if (lat >= 43.7 && lat <= 43.8 && lng >= 7.4 && lng <= 7.5) {
      return 'MC'; // Monaco
    }
    if (lat >= 41.9 && lat <= 41.9 && lng >= 12.4 && lng <= 12.5) {
      return 'VA'; // Vatican City
    }
    if (lat >= 43.9 && lat <= 44.0 && lng >= 12.4 && lng <= 12.5) {
      return 'SM'; // San Marino
    }
    if (lat >= 42.5 && lat <= 42.7 && lng >= 1.4 && lng <= 1.8) {
      return 'AD'; // Andorra
    }
    if (lat >= 47.1 && lat <= 47.3 && lng >= 9.5 && lng <= 9.6) {
      return 'LI'; // Liechtenstein
    }

    // South Asia - BANGLADESH FIRST! (Before China check)
    if (lat >= 20.5 && lat <= 26.8 && lng >= 88.0 && lng <= 92.7) {
      return 'BD'; // Bangladesh - Very specific boundaries
    }
    if (lat >= 23.0 && lat <= 37.1 && lng >= 60.9 && lng <= 77.8) {
      return 'PK'; // Pakistan
    }
    if (lat >= 26.3 && lat <= 30.4 && lng >= 80.1 && lng <= 88.2) {
      return 'NP'; // Nepal
    }
    if (lat >= 5.9 && lat <= 9.8 && lng >= 79.7 && lng <= 81.9) {
      return 'LK'; // Sri Lanka
    }
    if (lat >= 26.7 && lat <= 28.3 && lng >= 88.8 && lng <= 92.1) {
      return 'BT'; // Bhutan
    }
    if (lat >= 3.2 && lat <= 7.1 && lng >= 72.7 && lng <= 73.8) {
      return 'MV'; // Maldives
    }

    // Southeast Asia - Before China
    if (lat >= 4.0 && lat <= 7.4 && lng >= 114.1 && lng <= 119.3) {
      return 'BN'; // Brunei
    }
    if (lat >= 5.5 && lat <= 21.0 && lng >= 92.2 && lng <= 107.6) {
      return 'TH'; // Thailand
    }
    if (lat >= 8.5 && lat <= 23.5 && lng >= 102.1 && lng <= 109.5) {
      return 'VN'; // Vietnam
    }
    if (lat >= 10.0 && lat <= 23.0 && lng >= 100.0 && lng <= 107.7) {
      return 'LA'; // Laos
    }
    if (lat >= 10.4 && lat <= 14.7 && lng >= 102.3 && lng <= 107.6) {
      return 'KH'; // Cambodia
    }
    if (lat >= 9.8 && lat <= 28.5 && lng >= 92.2 && lng <= 101.2) {
      return 'MM'; // Myanmar
    }
    if (lat >= 0.8 && lat <= 7.5 && lng >= 99.6 && lng <= 119.3) {
      return 'MY'; // Malaysia
    }
    if (lat >= -11.0 && lat <= 6.0 && lng >= 95.0 && lng <= 141.0) {
      return 'ID'; // Indonesia
    }
    if (lat >= 4.6 && lat <= 21.1 && lng >= 116.9 && lng <= 126.6) {
      return 'PH'; // Philippines
    }
    if (lat >= -8.9 && lat <= -8.1 && lng >= 125.1 && lng <= 127.4) {
      return 'TL'; // East Timor/Timor-Leste
    }

    // East Asia - More specific boundaries
    if (lat >= 30.0 && lat <= 46.0 && lng >= 129.0 && lng <= 146.0) {
      return 'JP'; // Japan - More specific
    }
    if (lat >= 33.1 && lat <= 38.6 && lng >= 124.6 && lng <= 131.9) {
      return 'KR'; // South Korea - More specific
    }
    if (lat >= 37.7 && lat <= 43.0 && lng >= 124.3 && lng <= 130.7) {
      return 'KP'; // North Korea
    }
    if (lat >= 41.6 && lat <= 52.1 && lng >= 87.7 && lng <= 119.9) {
      return 'MN'; // Mongolia
    }
    if (lat >= 21.9 && lat <= 25.3 && lng >= 120.1 && lng <= 122.0) {
      return 'TW'; // Taiwan
    }
    if (lat >= 22.1 && lat <= 22.6 && lng >= 113.8 && lng <= 114.4) {
      return 'HK'; // Hong Kong
    }
    if (lat >= 22.1 && lat <= 22.2 && lng >= 113.5 && lng <= 113.6) {
      return 'MO'; // Macau
    }

    // NOW China - After all smaller countries are checked
    if (lat >= 18.2 && lat <= 53.6 && lng >= 73.5 && lng <= 134.8) {
      return 'CN'; // China - More specific boundaries, after smaller countries
    }

    // India - After Bangladesh, Nepal, Bhutan are checked
    if (lat >= 6.8 && lat <= 37.1 && lng >= 68.1 && lng <= 97.4) {
      return 'IN'; // India - After smaller South Asian countries
    }

    // Central Asia
    if (lat >= 40.1 && lat <= 55.5 && lng >= 46.5 && lng <= 87.4) {
      return 'KZ'; // Kazakhstan
    }
    if (lat >= 37.2 && lat <= 45.6 && lng >= 56.0 && lng <= 73.2) {
      return 'UZ'; // Uzbekistan
    }
    if (lat >= 35.1 && lat <= 40.6 && lng >= 52.5 && lng <= 66.7) {
      return 'TM'; // Turkmenistan
    }
    if (lat >= 36.7 && lat <= 41.0 && lng >= 67.4 && lng <= 75.2) {
      return 'TJ'; // Tajikistan
    }
    if (lat >= 39.2 && lat <= 43.3 && lng >= 69.3 && lng <= 80.3) {
      return 'KG'; // Kyrgyzstan
    }
    if (lat >= 28.4 && lat <= 38.5 && lng >= 29.4 && lng <= 74.9) {
      return 'AF'; // Afghanistan
    }

    // Caucasus
    if (lat >= 38.8 && lat <= 41.9 && lng >= 44.8 && lng <= 50.4) {
      return 'AZ'; // Azerbaijan
    }
    if (lat >= 38.8 && lat <= 41.3 && lng >= 43.4 && lng <= 46.8) {
      return 'AM'; // Armenia
    }
    if (lat >= 41.1 && lat <= 43.6 && lng >= 39.9 && lng <= 46.7) {
      return 'GE'; // Georgia
    }

    // Middle East
    if (lat >= 35.8 && lat <= 42.1 && lng >= 25.7 && lng <= 44.8) {
      return 'TR'; // Turkey
    }
    if (lat >= 31.2 && lat <= 33.4 && lng >= 34.3 && lng <= 35.9) {
      return 'IL'; // Israel
    }
    if (lat >= 31.2 && lat <= 32.5 && lng >= 34.2 && lng <= 35.6) {
      return 'PS'; // Palestine
    }
    if (lat >= 33.0 && lat <= 37.3 && lng >= 35.1 && lng <= 42.4) {
      return 'SY'; // Syria
    }
    if (lat >= 33.1 && lat <= 34.7 && lng >= 35.1 && lng <= 36.6) {
      return 'LB'; // Lebanon
    }
    if (lat >= 29.2 && lat <= 33.4 && lng >= 34.9 && lng <= 39.3) {
      return 'JO'; // Jordan
    }
    if (lat >= 29.1 && lat <= 37.4 && lng >= 44.2 && lng <= 48.8) {
      return 'IQ'; // Iraq
    }
    if (lat >= 25.1 && lat <= 39.8 && lng >= 44.0 && lng <= 63.3) {
      return 'IR'; // Iran
    }
    if (lat >= 16.0 && lat <= 32.2 && lng >= 34.6 && lng <= 55.7) {
      return 'SA'; // Saudi Arabia
    }
    if (lat >= 22.5 && lat <= 26.1 && lng >= 51.0 && lng <= 56.4) {
      return 'AE'; // United Arab Emirates
    }
    if (lat >= 24.5 && lat <= 26.2 && lng >= 50.7 && lng <= 51.7) {
      return 'QA'; // Qatar
    }
    if (lat >= 28.5 && lat <= 30.1 && lng >= 46.6 && lng <= 48.5) {
      return 'KW'; // Kuwait
    }
    if (lat >= 25.8 && lat <= 26.3 && lng >= 50.5 && lng <= 50.7) {
      return 'BH'; // Bahrain
    }
    if (lat >= 16.0 && lat <= 26.4 && lng >= 51.9 && lng <= 60.0) {
      return 'OM'; // Oman
    }
    if (lat >= 12.1 && lat <= 19.0 && lng >= 42.6 && lng <= 54.8) {
      return 'YE'; // Yemen
    }

    // South America - INCLUDING PARAGUAY FIRST! (Enhanced precision)
    if (lat >= -27.61 && lat <= -19.29 && lng >= -62.65 && lng <= -54.26) {
      return 'PY'; // Paraguay - ENHANCED precision, checked first!
    }
    if (lat >= -35.0 && lat <= -30.1 && lng >= -58.4 && lng <= -53.1) {
      return 'UY'; // Uruguay - after Paraguay
    }
    if (lat >= -22.9 && lat <= -9.7 && lng >= -69.6 && lng <= -57.5) {
      return 'BO'; // Bolivia - after Paraguay, enhanced boundaries
    }
    if (lat >= -34.0 && lat <= -5.3 && lng >= -74.0 && lng <= -34.8) {
      return 'BR'; // Brazil - after smaller countries
    }
    if (lat >= -55.1 && lat <= -21.8 && lng >= -73.6 && lng <= -53.6) {
      return 'AR'; // Argentina - checked after Paraguay and Uruguay
    }
    if (lat >= -56.0 && lat <= -17.5 && lng >= -75.6 && lng <= -66.4) {
      return 'CL'; // Chile
    }
    if (lat >= -18.4 && lat <= -0.0 && lng >= -81.3 && lng <= -68.7) {
      return 'PE'; // Peru
    }
    if (lat >= -4.2 && lat <= 1.5 && lng >= -81.1 && lng <= -75.2) {
      return 'EC'; // Ecuador
    }
    if (lat >= -4.2 && lat <= 15.9 && lng >= -79.0 && lng <= -66.9) {
      return 'CO'; // Colombia
    }
    if (lat >= 0.6 && lat <= 15.9 && lng >= -73.4 && lng <= -59.8) {
      return 'VE'; // Venezuela
    }
    if (lat >= 1.2 && lat <= 8.6 && lng >= -61.4 && lng <= -56.5) {
      return 'GY'; // Guyana
    }
    if (lat >= 1.8 && lat <= 6.0 && lng >= -58.1 && lng <= -53.9) {
      return 'SR'; // Suriname
    }
    if (lat >= 2.1 && lat <= 5.8 && lng >= -54.6 && lng <= -51.6) {
      return 'GF'; // French Guiana
    }

    // North America
    if (lat >= 25.1 && lat <= 49.4 && lng >= -125.0 && lng <= -66.9) {
      return 'US'; // Continental United States
    }
    if (lat >= 41.7 && lat <= 83.1 && lng >= -141.0 && lng <= -52.6) {
      return 'CA'; // Canada
    }
    if (lat >= 14.5 && lat <= 32.7 && lng >= -118.4 && lng <= -86.7) {
      return 'MX'; // Mexico
    }

    // Central America
    if (lat >= 13.7 && lat <= 17.8 && lng >= -92.3 && lng <= -88.2) {
      return 'GT'; // Guatemala
    }
    if (lat >= 15.9 && lat <= 18.5 && lng >= -89.2 && lng <= -87.8) {
      return 'BZ'; // Belize
    }
    if (lat >= 13.1 && lat <= 14.4 && lng >= -90.1 && lng <= -87.7) {
      return 'SV'; // El Salvador
    }
    if (lat >= 12.9 && lat <= 16.0 && lng >= -89.4 && lng <= -83.1) {
      return 'HN'; // Honduras
    }
    if (lat >= 10.7 && lat <= 15.0 && lng >= -87.7 && lng <= -82.7) {
      return 'NI'; // Nicaragua
    }
    if (lat >= 8.0 && lat <= 11.2 && lng >= -85.9 && lng <= -82.6) {
      return 'CR'; // Costa Rica
    }
    if (lat >= 7.2 && lat <= 9.6 && lng >= -83.1 && lng <= -77.2) {
      return 'PA'; // Panama
    }

    // Caribbean
    if (lat >= 19.8 && lat <= 23.3 && lng >= -85.0 && lng <= -74.1) {
      return 'CU'; // Cuba
    }
    if (lat >= 17.7 && lat <= 18.5 && lng >= -78.4 && lng <= -76.2) {
      return 'JM'; // Jamaica
    }
    if (lat >= 18.0 && lat <= 20.1 && lng >= -74.5 && lng <= -68.3) {
      return 'HT'; // Haiti
    }
    if (lat >= 17.6 && lat <= 19.9 && lng >= -72.0 && lng <= -68.3) {
      return 'DO'; // Dominican Republic
    }
    if (lat >= 17.9 && lat <= 18.5 && lng >= -67.3 && lng <= -65.2) {
      return 'PR'; // Puerto Rico
    }
    if (lat >= 10.0 && lat <= 11.4 && lng >= -61.9 && lng <= -60.5) {
      return 'TT'; // Trinidad and Tobago
    }
    if (lat >= 13.0 && lat <= 13.3 && lng >= -59.7 && lng <= -59.4) {
      return 'BB'; // Barbados
    }

    // United Kingdom and Ireland
    if (lat >= 49.9 && lat <= 60.9 && lng >= -8.2 && lng <= 1.8) {
      return 'GB'; // United Kingdom
    }
    if (lat >= 51.4 && lat <= 55.4 && lng >= -10.5 && lng <= -5.9) {
      return 'IE'; // Ireland
    }

    // Western Europe
    if (lat >= 41.3 && lat <= 51.1 && lng >= -5.1 && lng <= 9.6) {
      return 'FR'; // France
    }
    if (lat >= 47.3 && lat <= 55.1 && lng >= 5.9 && lng <= 15.0) {
      return 'DE'; // Germany
    }
    if (lat >= 35.2 && lat <= 43.8 && lng >= -9.3 && lng <= 4.3) {
      return 'ES'; // Spain
    }
    if (lat >= 36.6 && lat <= 47.1 && lng >= 6.6 && lng <= 18.5) {
      return 'IT'; // Italy
    }
    if (lat >= 50.7 && lat <= 53.6 && lng >= 3.4 && lng <= 7.2) {
      return 'NL'; // Netherlands
    }
    if (lat >= 49.5 && lat <= 51.5 && lng >= 2.5 && lng <= 6.4) {
      return 'BE'; // Belgium
    }
    if (lat >= 45.8 && lat <= 47.8 && lng >= 5.9 && lng <= 10.5) {
      return 'CH'; // Switzerland
    }
    if (lat >= 46.4 && lat <= 49.0 && lng >= 9.5 && lng <= 17.2) {
      return 'AT'; // Austria
    }
    if (lat >= 39.2 && lat <= 42.2 && lng >= -9.5 && lng <= -6.2) {
      return 'PT'; // Portugal
    }
    if (lat >= 49.4 && lat <= 50.2 && lng >= 5.7 && lng <= 6.5) {
      return 'LU'; // Luxembourg
    }

    // Northern Europe
    if (lat >= 55.3 && lat <= 69.1 && lng >= 10.9 && lng <= 24.2) {
      return 'SE'; // Sweden
    }
    if (lat >= 57.9 && lat <= 71.2 && lng >= 4.6 && lng <= 31.3) {
      return 'NO'; // Norway
    }
    if (lat >= 54.5 && lat <= 57.8 && lng >= 8.0 && lng <= 15.2) {
      return 'DK'; // Denmark
    }
    if (lat >= 59.8 && lat <= 70.1 && lng >= 19.1 && lng <= 31.6) {
      return 'FI'; // Finland
    }
    if (lat >= 63.4 && lat <= 66.6 && lng >= -25.0 && lng <= -13.5) {
      return 'IS'; // Iceland
    }

    // Baltic States
    if (lat >= 57.5 && lat <= 59.7 && lng >= 21.8 && lng <= 28.2) {
      return 'EE'; // Estonia
    }
    if (lat >= 55.7 && lat <= 58.1 && lng >= 20.9 && lng <= 28.2) {
      return 'LV'; // Latvia
    }
    if (lat >= 53.9 && lat <= 56.5 && lng >= 20.9 && lng <= 26.9) {
      return 'LT'; // Lithuania
    }

    // Central and Eastern Europe
    if (lat >= 49.0 && lat <= 54.9 && lng >= 14.1 && lng <= 24.1) {
      return 'PL'; // Poland
    }
    if (lat >= 48.5 && lat <= 51.1 && lng >= 12.1 && lng <= 18.9) {
      return 'CZ'; // Czech Republic
    }
    if (lat >= 47.7 && lat <= 49.6 && lng >= 16.8 && lng <= 22.6) {
      return 'SK'; // Slovakia
    }
    if (lat >= 45.7 && lat <= 48.6 && lng >= 16.1 && lng <= 22.9) {
      return 'HU'; // Hungary
    }
    if (lat >= 43.6 && lat <= 48.3 && lng >= 20.3 && lng <= 29.7) {
      return 'RO'; // Romania
    }
    if (lat >= 41.2 && lat <= 44.2 && lng >= 22.4 && lng <= 28.6) {
      return 'BG'; // Bulgaria
    }
    if (lat >= 45.4 && lat <= 46.9 && lng >= 13.4 && lng <= 16.6) {
      return 'SI'; // Slovenia
    }
    if (lat >= 42.4 && lat <= 46.5 && lng >= 13.5 && lng <= 19.4) {
      return 'HR'; // Croatia
    }
    if (lat >= 42.2 && lat <= 46.2 && lng >= 18.8 && lng <= 23.0) {
      return 'RS'; // Serbia
    }
    if (lat >= 43.2 && lat <= 45.3 && lng >= 15.7 && lng <= 19.6) {
      return 'BA'; // Bosnia and Herzegovina
    }
    if (lat >= 42.4 && lat <= 43.6 && lng >= 18.4 && lng <= 20.4) {
      return 'ME'; // Montenegro
    }
    if (lat >= 40.8 && lat <= 42.4 && lng >= 20.5 && lng <= 23.0) {
      return 'MK'; // North Macedonia
    }
    if (lat >= 39.6 && lat <= 42.7 && lng >= 19.3 && lng <= 21.1) {
      return 'AL'; // Albania
    }
    if (lat >= 42.2 && lat <= 43.3 && lng >= 20.0 && lng <= 21.8) {
      return 'XK'; // Kosovo
    }
    if (lat >= 45.5 && lat <= 48.5 && lng >= 26.6 && lng <= 30.1) {
      return 'MD'; // Moldova
    }

    // Southern Europe
    if (lat >= 34.8 && lat <= 41.8 && lng >= 19.6 && lng <= 28.2) {
      return 'GR'; // Greece
    }
    if (lat >= 34.6 && lat <= 35.7 && lng >= 32.3 && lng <= 34.6) {
      return 'CY'; // Cyprus
    }
    if (lat >= 35.8 && lat <= 36.1 && lng >= 14.2 && lng <= 14.6) {
      return 'MT'; // Malta
    }

    // Eastern Europe
    if (lat >= 44.4 && lat <= 52.4 && lng >= 22.1 && lng <= 40.2) {
      return 'UA'; // Ukraine
    }
    if (lat >= 51.3 && lat <= 56.2 && lng >= 23.2 && lng <= 32.8) {
      return 'BY'; // Belarus
    }
    if (lat >= 41.2 && lat <= 82.0 && lng >= 19.6 && lng <= 180.0) {
      return 'RU'; // Russia (very large - checked last)
    }

    // Africa - Comprehensive coverage
    if (lat >= 22.0 && lat <= 31.7 && lng >= 25.0 && lng <= 37.0) {
      return 'EG'; // Egypt
    }
    if (lat >= 19.5 && lat <= 33.2 && lng >= 9.4 && lng <= 25.2) {
      return 'LY'; // Libya
    }
    if (lat >= 30.2 && lat <= 37.5 && lng >= 7.5 && lng <= 11.6) {
      return 'TN'; // Tunisia
    }
    if (lat >= 18.9 && lat <= 37.1 && lng >= -8.7 && lng <= 12.0) {
      return 'DZ'; // Algeria
    }
    if (lat >= 20.8 && lat <= 35.9 && lng >= -17.1 && lng <= -1.0) {
      return 'MA'; // Morocco
    }
    if (lat >= 8.7 && lat <= 22.0 && lng >= 21.8 && lng <= 38.6) {
      return 'SD'; // Sudan
    }
    if (lat >= 3.5 && lat <= 12.2 && lng >= 23.9 && lng <= 35.9) {
      return 'SS'; // South Sudan
    }
    if (lat >= 3.4 && lat <= 14.9 && lng >= 32.9 && lng <= 48.0) {
      return 'ET'; // Ethiopia
    }
    if (lat >= 12.4 && lat <= 18.0 && lng >= 36.4 && lng <= 43.1) {
      return 'ER'; // Eritrea
    }
    if (lat >= -1.7 && lat <= 11.9 && lng >= 41.0 && lng <= 51.4) {
      return 'SO'; // Somalia
    }
    if (lat >= 11.3 && lat <= 12.7 && lng >= 41.7 && lng <= 43.4) {
      return 'DJ'; // Djibouti
    }
    if (lat >= -4.7 && lat <= 4.6 && lng >= 33.9 && lng <= 41.9) {
      return 'KE'; // Kenya
    }
    if (lat >= -1.5 && lat <= 4.2 && lng >= 29.6 && lng <= 35.0) {
      return 'UG'; // Uganda
    }
    if (lat >= -11.7 && lat <= -0.9 && lng >= 29.3 && lng <= 40.4) {
      return 'TZ'; // Tanzania
    }
    if (lat >= -2.8 && lat <= -1.0 && lng >= 28.9 && lng <= 30.9) {
      return 'RW'; // Rwanda
    }
    if (lat >= -4.5 && lat <= -2.3 && lng >= 29.0 && lng <= 30.8) {
      return 'BI'; // Burundi
    }

    // Asia Pacific
    if (lat >= -44.0 && lat <= -10.0 && lng >= 113.0 && lng <= 154.0) {
      return 'AU'; // Australia
    }
    if (lat >= -47.0 && lat <= -34.0 && lng >= 166.0 && lng <= 179.0) {
      return 'NZ'; // New Zealand
    }

    return null;
  } catch (error) {
    console.warn('Error getting country from geolocation:', error);
    return null;
  }
};

/**
 * Detect country from listing location data with Mapbox reverse geocoding
 * @param {Object} listingLocation - Listing location object
 * @param {string} listingLocation.address - Address string
 * @param {Object} listingLocation.geolocation - Geolocation object
 * @returns {Promise<string|null>} Promise that resolves to country code or null
 */
export const detectCountryFromListing = async listingLocation => {
  if (!listingLocation) {
    return null;
  }

  const { address, geolocation } = listingLocation;

  // Method 1: Try address-based detection first (most reliable)
  if (address) {
    const countryFromAddress = getCountryFromAddress(address);
    if (countryFromAddress) {
      return countryFromAddress;
    }
  }

  // Method 2: If no address or address detection failed, try Mapbox reverse geocoding
  if (geolocation && geolocation.lat && geolocation.lng) {
    try {
      const reversedAddress = await reverseGeocode(geolocation.lat, geolocation.lng);
      if (reversedAddress) {
        const countryFromReversedAddress = getCountryFromAddress(reversedAddress);
        if (countryFromReversedAddress) {
          return countryFromReversedAddress;
        }
      }
    } catch (error) {
      console.warn('Mapbox reverse geocoding failed:', error);
    }
  }

  // Method 3: Fallback to geolocation boundary detection
  if (geolocation) {
    const countryFromGeo = await getCountryFromGeolocation(geolocation);
    if (countryFromGeo) {
      return countryFromGeo;
    }
  }

  return null;
};

/**
 * Create a country flag component data using only location information
 * @param {string} countryCode - ISO 3166-1 alpha-2 country code
 * @returns {Object|null} Object with flag emoji, country name, and country code
 */
export const createCountryFlagData = countryCode => {
  if (!countryCode) {
    return null;
  }

  const flag = getFlagEmoji(countryCode);
  const countryName = getCountryName(countryCode);

  if (!flag) {
    return null;
  }

  const flagData = {
    countryCode: countryCode.toUpperCase(),
    flag,
    countryName: countryName || countryCode.toUpperCase(), // Fallback to country code if name not found
  };

  return flagData;
};

/**
 * Get flag data for a listing based on its location (no currency information)
 * @param {Object} listing - Listing object
 * @returns {Promise<Object|null>} Promise that resolves to flag data or null
 */
export const getListingCountryFlag = async listing => {
  if (!listing || !listing.attributes) {
    return null;
  }

  const { publicData, geolocation } = listing.attributes;
  const location = publicData?.location;

  if (!location && !geolocation) {
    return null;
  }

  const listingLocation = {
    address: location?.address,
    geolocation,
  };

  const countryCode = await detectCountryFromListing(listingLocation);
  if (!countryCode) {
    return null;
  }

  return createCountryFlagData(countryCode);
};
